import { useCallback, useEffect } from 'react'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type ExpenseReportFormSchema } from '@webapp/modules/expense/components/ExpenseReportForm'
import { type Expense } from '@webapp/modules/expense/entities/Expense'
import messages from './messages'
import { useMutateEditExpenseReport } from '../../models/useMutateEditExpenseReport'

interface UseEditExpenseReportPresenterParams {
  expense: Expense
  onSuccess: () => void
  onSubmittingChange?: (isSubmitting: boolean) => void
}

export const useEditExpenseReportPresenter = ({
  expense,
  onSuccess,
  onSubmittingChange,
}: UseEditExpenseReportPresenterParams) => {
  const intl = useProcurifyIntl()
  const { mutate, isLoading } = useMutateEditExpenseReport()

  useEffect(() => {
    onSubmittingChange?.(isLoading)
  }, [isLoading, onSubmittingChange])

  const handleSubmit = useCallback(
    async (values: ExpenseReportFormSchema) => {
      try {
        await mutate({
          expenseId: expense.id,
          uuid: expense.uuid,
          payload: {
            title: values.title,
            travel: values.travel?.id ?? null,
            notes: values.notes,
          },
        })

        toast.success({
          title: intl.formatMessage(messages.successTitle),
          children: intl.formatMessage(messages.successMessage),
        })

        window.analytics?.track('PendingExpense Edited', { editor: 'approver' })

        onSuccess()
      } catch (err) {
        const errorMessage = getMessageFromError(err)

        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children: errorMessage,
        })
      }
    },
    [expense.id, expense.uuid, intl, mutate, onSuccess]
  )

  return {
    states: {
      isSubmitting: isLoading,
    },
    methods: {
      handleSubmit,
    },
  }
}
